import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import api, { euro } from "@/lib/api";
import { StatCard, Badge } from "@/components/kit";
import { TrendingUp, FileClock, Users, AlertTriangle, UserCog, Boxes } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats)
    return <div className="animate-pulse font-mono text-sm text-muted-foreground">Chargement des données…</div>;

  const cards = [
    { label: "Chiffre d'affaires", value: euro(stats.revenue), sub: "Factures payées", icon: TrendingUp, accent: "bg-emerald-500/15 text-emerald-400", testid: "kpi-revenue" },
    { label: "Factures en attente", value: euro(stats.pending_amount), sub: `${stats.pending_count} document(s)`, icon: FileClock, accent: "bg-blue-500/15 text-blue-400", testid: "kpi-pending" },
    { label: "Clients & prospects", value: stats.total_clients + stats.total_prospects, sub: `${stats.total_clients} clients · ${stats.total_prospects} prospects`, icon: Users, accent: "bg-primary/15 text-primary", testid: "kpi-clients" },
    { label: "Alertes stock", value: stats.low_stock_count, sub: `${stats.total_products} produits au total`, icon: AlertTriangle, accent: "bg-destructive/15 text-destructive", testid: "kpi-stock" },
  ];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Revenue chart */}
        <div className="border border-border bg-card p-6 lg:col-span-2">
          <h3 className="mb-6 font-display text-lg font-bold">Évolution du chiffre d'affaires</h3>
          {stats.revenue_series.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={stats.revenue_series} margin={{ left: -10 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(38 92% 50%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(38 92% 50%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" vertical={false} />
                <XAxis dataKey="month" stroke="hsl(240 5% 64.9%)" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(240 5% 64.9%)" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "hsl(240 10% 3.9%)", border: "1px solid hsl(240 3.7% 15.9%)", borderRadius: 0, fontSize: 12 }}
                  formatter={(v) => euro(v)}
                />
                <Area type="monotone" dataKey="total" stroke="hsl(38 92% 50%)" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
              Aucune facture payée pour l'instant. Le graphique se remplira automatiquement.
            </div>
          )}
        </div>

        {/* Side widgets */}
        <div className="space-y-6">
          <div className="border border-border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <UserCog size={18} className="text-primary" />
              <h3 className="font-display text-base font-bold">Ressources humaines</h3>
            </div>
            <div className="flex items-center justify-between border-b border-border py-2 text-sm">
              <span className="text-muted-foreground">Employés</span>
              <span className="font-mono">{stats.total_employees}</span>
            </div>
            <div className="flex items-center justify-between py-2 text-sm">
              <span className="text-muted-foreground">Congés en attente</span>
              <span className="font-mono text-primary">{stats.pending_leaves}</span>
            </div>
          </div>

          <div className="border border-border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Boxes size={18} className="text-destructive" />
              <h3 className="font-display text-base font-bold">Stock faible</h3>
            </div>
            {stats.low_stock_items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune alerte. Tout est approvisionné.</p>
            ) : (
              stats.low_stock_items.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
                  <span className="truncate">{p.name}</span>
                  <span className="font-mono text-destructive">{p.quantity} {p.unit}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent invoices */}
      <div className="border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 className="font-display text-lg font-bold">Documents récents</h3>
          <Link to="/facturation" className="text-sm text-primary hover:underline">Tout voir</Link>
        </div>
        {stats.recent_invoices.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Aucun document. Créez votre première facture ou devis.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-6 py-3 font-medium">Numéro</th>
                <th className="px-6 py-3 font-medium">Client</th>
                <th className="px-6 py-3 font-medium">Statut</th>
                <th className="px-6 py-3 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_invoices.map((i) => (
                <tr key={i.id} className="border-b border-border transition-colors last:border-0 hover:bg-accent/50">
                  <td className="px-6 py-3 font-mono text-xs">{i.number}</td>
                  <td className="px-6 py-3">{i.client_name || "—"}</td>
                  <td className="px-6 py-3"><Badge>{i.status}</Badge></td>
                  <td className="px-6 py-3 text-right font-mono">{euro(i.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
